// /reports — first-class PIXEL-PERFECT documents: invoices, statements, filings.
//
// Same explorer idiom as Apps / Agents / Models: a left rail of rows grouped by
// ownership, a per-row ⋯ menu, and a detail pane on the right. Deliberately
// identical structure, because a report is another agent-creatable first-class
// component and should not need re-learning.
//
// What is different from Models: the artifact is a TYPST TEMPLATE — plain text.
// So the detail pane is an editor plus a render button, and the version history
// panel shows real diffs of the template rather than opaque revisions (the
// manifest is a fork endpoint, so /api/versions/* covers it with no report-
// specific code).
import React, { useEffect, useState } from "react";
import {
  FileText, Loader2, RefreshCw, Plus, Globe, Lock, MoreVertical, Trash2,
  Wrench, PanelLeftClose, PanelLeftOpen, Play, Download, AlertTriangle, History,
  Sparkles, Box, MessageSquare, X,
} from "lucide-react";
import { listReports, getReport, createReport, updateReport, deleteReport, reportEngine } from "../services/reports";
import { listAppRequests } from "../services/apps";
import AppRequestDialog from "../components/AppRequestDialog";
import VersionHistory from "../components/VersionHistory";
import { useProject } from "@/contexts/ProjectContext";
import { isPortal } from "../utils/portal";

const btnGhost = "inline-flex items-center gap-1.5 rounded-md text-sm px-3 py-1.5 border border-card-border bg-transparent text-page-text cursor-pointer hover:bg-card-border/30 disabled:opacity-50";
const btnPrimary = "inline-flex items-center gap-1.5 rounded-md bg-nav-active text-white text-sm px-3 py-1.5 border-0 cursor-pointer disabled:opacity-50";
const inputCls = "w-full rounded-md border border-card-border bg-page-bg px-3 py-2 text-sm text-page-text outline-none focus:border-nav-active";

// Report request kinds for the shared AppRequestDialog — same machinery Apps and
// Agents use, so a "build me this report" brief lands on the Board like any other.
const REPORT_REQUEST_KINDS = [
  { kind: "new_report", label: "New report", icon: Sparkles,
    hint: "Describe the document you need — an agent writes the template and checks it renders.",
    titlePlaceholder: "What is the document? e.g. Monthly customer statement",
    detailsPlaceholder: "Describe the document in plain language: what it shows, which data, how it should be laid out, any totals or grouping…" },
  { kind: "change", label: "Build a change", icon: Wrench,
    hint: "Describe what should change about this report.",
    needsTarget: true, targetLabel: "Select a report…",
    titlePlaceholder: "Short summary",
    detailsPlaceholder: "Describe the change — new columns, different grouping, a total, page layout…" },
];

export default function Reports() {
  const { projectSlug, sharedSurfaceOnly } = useProject();
  const canBuild = !isPortal() && !sharedSurfaceOnly;

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [notProvisioned, setNotProvisioned] = useState(false);
  const [selected, setSelected] = useState(null);   // { report, manifest, can_write }
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(null);
  const [flash, setFlash] = useState(null);
  const [menuId, setMenuId] = useState(null);
  const [requestDialog, setRequestDialog] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [openRequests, setOpenRequests] = useState(0);

  // Same persisted-rail convention as appsRailOpen / modelsRailOpen.
  const [railOpen, setRailOpen] = useState(() => {
    try { return localStorage.getItem("reportsRailOpen") !== "0"; } catch { return true; }
  });
  const toggleRail = () => setRailOpen((o) => {
    const n = !o;
    try { localStorage.setItem("reportsRailOpen", n ? "1" : "0"); } catch { /* private mode */ }
    return n;
  });

  async function load() {
    setLoading(true); setErr(null);
    try {
      const r = await listReports();
      setReports(r?.reports || []);
      setNotProvisioned(false);
    } catch (e) {
      // A project whose backend predates this surface (or has no fork) should read
      // as "not set up here", not as a red error.
      const msg = String(e?.message || e);
      if (/404|not found|Invalid table name|relation .* does not exist/i.test(msg)) setNotProvisioned(true);
      else setErr(msg);
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectSlug]);

  useEffect(() => {
    let alive = true;
    listAppRequests({ status: "open" })
      .then((r) => { if (alive) setOpenRequests((r?.requests || []).length); })
      .catch(() => {});
    return () => { alive = false; };
  }, [requestDialog]);

  useEffect(() => {
    if (menuId == null) return;
    const close = () => setMenuId(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [menuId]);

  async function open(r) {
    try { setSelected(await getReport(r.id)); }
    catch (e) { setErr(String(e?.message || e)); }
  }

  const displayName = (r) => r.display_name || r.name;
  const mine = reports.filter((r) => r.is_owner);
  const shared = reports.filter((r) => !r.is_owner && r.shared_with_me);
  const org = reports.filter((r) => !r.is_owner && !r.shared_with_me);

  const Section = ({ label, children }) => (
    <div className="mb-2">
      <div className="px-2 pt-2 pb-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-page-muted">{label}</span>
      </div>
      {children}
    </div>
  );

  const Row = ({ r }) => {
    const active = selected && selected.report.id === r.id;
    const menuOpen = menuId === r.id;
    return (
      <div
        data-testid={`report-${r.id}`}
        data-report-name={r.name}
        className={"group relative w-full rounded-md text-sm " +
          (active ? "bg-nav-active text-white" : "text-page-text hover:bg-card-border/40")}
      >
        <div className="flex items-center gap-1">
          <button
            type="button"
            data-testid={`report-open-${r.id}`}
            onClick={() => open(r)}
            className="flex-1 min-w-0 text-left px-3 py-2 flex items-center gap-2 cursor-pointer border-0 bg-transparent text-inherit"
          >
            <span className="shrink-0 w-[16px] text-center leading-none" aria-hidden>{r.icon || "📄"}</span>
            <span className="min-w-0 flex flex-col">
              <span className="flex items-center gap-1.5">
                <span className="overflow-hidden text-ellipsis whitespace-nowrap">{displayName(r)}</span>
                {r.visibility === "org"
                  ? <Globe size={12} className="shrink-0 opacity-60" title="Visible to everyone in the organization" />
                  : <Lock size={12} className="shrink-0 opacity-60" title="Private" />}
              </span>
              <span className={"text-xs overflow-hidden text-ellipsis whitespace-nowrap " + (active ? "text-white/70" : "text-page-muted")}>
                {r.description || "Typst → PDF"}
              </span>
            </span>
          </button>
          <button
            type="button"
            data-testid={`report-menu-${r.id}`}
            title="Actions"
            onClick={(e) => { e.stopPropagation(); setMenuId(menuOpen ? null : r.id); }}
            className={"shrink-0 mr-1 p-1 rounded cursor-pointer border-0 bg-transparent " +
              (active ? "text-white/80 hover:text-white" : "text-page-muted hover:text-page-text")}
          >
            <MoreVertical size={14} />
          </button>
        </div>
        {menuOpen && (
          <div
            className="absolute right-1 top-full z-40 mt-1 w-48 rounded-md border border-card-border bg-card-bg shadow-lg py-1 text-page-text"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Reachable WITHOUT opening the report — same reasoning as Apps:
                "what changed" is exactly the question when a report breaks. */}
            <MenuItem icon={History} label="Version history"
                      onClick={() => { setMenuId(null); setHistoryTarget(r); }} />
            {canBuild && (
              <MenuItem icon={Wrench} label="Build a change"
                        onClick={() => { setMenuId(null); setRequestDialog({ kind: "change", report: r }); }} />
            )}
            {r.can_manage && (
              <MenuItem icon={Trash2} label="Remove" danger
                        onClick={async () => {
                          setMenuId(null);
                          if (!window.confirm(`Remove report '${displayName(r)}'?`)) return;
                          try {
                            await deleteReport(r.id);
                            if (selected?.report?.id === r.id) setSelected(null);
                            load();
                          } catch (e) { setErr(String(e?.message || e)); }
                        }} />
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="h-full w-full flex bg-page-bg" data-testid="reports-page">
      {/* ── rail ────────────────────────────────────────────────────────────── */}
      {railOpen && (
        <div className="w-[280px] shrink-0 border-r border-card-border bg-card-bg flex flex-col">
          <div className="flex items-center gap-2 px-3 h-12 border-b border-card-border">
            <FileText size={16} className="text-nav-active" />
            <span className="font-semibold text-page-text">Reports</span>
            <div className="flex-1" />
            <button type="button" title="Refresh" onClick={load}
                    className="p-1.5 rounded hover:bg-page-bg text-page-muted border-0 bg-transparent cursor-pointer">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            </button>
            <button type="button" title="Hide list" onClick={toggleRail}
                    className="p-1.5 rounded hover:bg-page-bg text-page-muted border-0 bg-transparent cursor-pointer">
              <PanelLeftClose size={14} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {err && <div className="text-xs text-red-500 px-2 py-2">{err}</div>}
            {notProvisioned && (
              <div className="text-sm text-page-muted px-2 py-3">
                Reports aren't set up in this project yet.
              </div>
            )}
            {!loading && !notProvisioned && reports.length === 0 && !err && (
              <div className="text-sm text-page-muted px-2 py-3">
                No reports yet. Create one below, or ask an agent to build it.
              </div>
            )}
            {mine.length > 0 && <Section label="My reports">{mine.map((r) => <Row key={r.id} r={r} />)}</Section>}
            {shared.length > 0 && <Section label="Shared with me">{shared.map((r) => <Row key={r.id} r={r} />)}</Section>}
            {org.length > 0 && <Section label="Organization">{org.map((r) => <Row key={r.id} r={r} />)}</Section>}
          </div>
          {canBuild && (
            <div className="p-2 border-t border-card-border">
              <button type="button" data-testid="report-new" className={btnGhost + " w-full justify-center"}
                      onClick={() => setRequestDialog({ kind: "new_report" })}>
                <Sparkles size={14} /> New report
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── detail ──────────────────────────────────────────────────────────── */}
      <div className="flex-1 min-w-0 flex flex-col">
        {!railOpen && (
          <div className="h-12 flex items-center px-3 border-b border-card-border">
            <button type="button" title="Show list" onClick={toggleRail}
                    className="p-1.5 rounded hover:bg-card-border/40 text-page-muted border-0 bg-transparent cursor-pointer">
              <PanelLeftOpen size={16} />
            </button>
          </div>
        )}
        {flash && (
          <div className={"px-4 py-2 text-sm " + (flash.kind === "error" ? "text-red-500" : "text-emerald-600")}>
            {flash.text}
          </div>
        )}
        {selected
          ? <ReportDetail
              key={selected.report.id}
              data={selected}
              busy={busy} setBusy={setBusy}
              onFlash={setFlash}
              onChanged={async () => { setSelected(await getReport(selected.report.id)); load(); }}
              onRequestChange={() => setRequestDialog({ kind: "change", report: selected.report })}
            />
          : (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-6">
              <FileText size={34} className="text-page-muted" />
              <div className="text-lg font-medium text-page-title">Your project reports</div>
              <p className="text-sm text-page-muted max-w-[520px]">
                Pixel-perfect documents — invoices, statements, filings — rendered from a
                template over this project's governed data. Pick one from the left, or
                create one and describe it to an agent.
              </p>
            </div>
          )}
      </div>

      {creating && (
        <CreateReportDialog
          onClose={() => setCreating(false)}
          onCreated={async (row) => { setCreating(false); await load(); if (row?.id) open(row); }}
        />
      )}
      {historyTarget && (
        <Modal title={`Version history — ${displayName(historyTarget)}`} width={560}
               onClose={() => setHistoryTarget(null)}>
          <VersionHistory kind="report" name={historyTarget.name}
                          canRestore={!!historyTarget.can_manage}
                          onRestored={() => { setHistoryTarget(null); load(); }} />
        </Modal>
      )}
      {requestDialog && (
        <AppRequestDialog
          kinds={REPORT_REQUEST_KINDS}
          initial={{ kind: requestDialog.kind, targetId: requestDialog.report?.id ?? null }}
          onClose={() => setRequestDialog(null)}
          onCreated={() => setRequestDialog(null)}
        />
      )}
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, danger }) {
  return (
    <button type="button" onClick={onClick}
      className={"w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 cursor-pointer border-0 bg-transparent " +
        (danger ? "text-red-500 hover:bg-red-500/10" : "text-page-text hover:bg-card-border/40")}>
      <Icon size={14} className="shrink-0 opacity-80" /> {label}
    </button>
  );
}

function Modal({ title, onClose, children, width = 480 }) {
  // Caps its height and scrolls the BODY, header pinned — the overlay is `fixed`,
  // so an overflowing dialog would otherwise be unreachable.
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-card-bg border border-card-border rounded-xl shadow-xl p-5 flex flex-col"
           style={{ width, maxWidth: "calc(100vw - 32px)", maxHeight: "85vh" }}>
        <div className="flex items-center justify-between mb-4 shrink-0">
          <div className="font-semibold text-page-text truncate pr-2">{title}</div>
          <button type="button" onClick={onClose}
                  className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1 shrink-0">✕</button>
        </div>
        <div className="overflow-y-auto min-h-0">{children}</div>
      </div>
    </div>
  );
}

// ── detail: template editor + render ────────────────────────────────────────
function ReportDetail({ data, busy, setBusy, onFlash, onChanged, onRequestChange }) {
  const { report, manifest, can_write: canWrite } = data;
  const [template, setTemplate] = useState(manifest?.template || "");
  const [query, setQuery] = useState(manifest?.base_query || manifest?.data_query || "");
  // The DECLARED contract: what the template consumes, and what varies per run.
  const fields = manifest?.fields || [];
  const declaredParams = manifest?.params || [];
  // Param VALUES for a render — this is how the same template produces a different
  // document ("the September statement for Unit 2") without new SQL.
  const [paramValues, setParamValues] = useState(() => {
    const init = {};
    for (const p of (manifest?.params || [])) if (p.default != null) init[p.name] = String(p.default);
    return init;
  });
  const [dirty, setDirty] = useState(false);
  // Typst compile errors are precise diagnostics (line + caret), which is exactly
  // what makes the edit loop tractable — so show them verbatim, not summarised.
  const [diag, setDiag] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  // The technical surfaces live on TABS — a report page leads with the DOCUMENT,
  // not with its plumbing.
  const [tab, setTab] = useState("document");
  // LAYOUT PREVIEW BY DEFAULT: the template rendered over placeholder rows
  // derived from the declared fields. No query runs, no source is needed — the
  // page shows what the document LOOKS like before anyone renders real data.
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewErr, setPreviewErr] = useState(null);
  useEffect(() => {
    let gone = false;
    setPreviewUrl(null); setPreviewErr(null);
    reportEngine("preview", { name: report.name })
      .then((r) => { if (!gone) setPreviewUrl(r?.data?.url || r?.data?.signed_url || null); })
      .catch((e) => { if (!gone) setPreviewErr(String(e?.message || e)); });
    return () => { gone = true; };
  }, [report.id]);

  const save = async () => {
    setBusy("save"); setDiag(null);
    try {
      await updateReport(report.id, { template, base_query: query || null });
      setDirty(false);
      onFlash({ kind: "ok", text: "Saved." });
      await onChanged();
    } catch (e) { onFlash({ kind: "error", text: String(e?.message || e) }); }
    finally { setBusy(null); }
  };

  const render = async () => {
    setBusy("render"); setDiag(null); setPdfUrl(null);
    try {
      const r = await reportEngine("render", { name: report.name, params: paramValues });
      const url = r?.data?.url || r?.data?.signed_url || null;
      if (url) setPdfUrl(url);
      else onFlash({ kind: "ok", text: "Rendered, but the engine returned no URL." });
    } catch (e) {
      // 422 = the template did not compile. That is the useful case: show it.
      setDiag(String(e?.message || e));
    } finally { setBusy(null); }
  };

  return (
    <div className="flex-1 min-w-0 overflow-y-auto p-5 flex flex-col gap-4">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-page-text truncate">
            {report.display_name || report.name}
          </h1>
          <div className="text-sm text-page-muted">{report.description || "Typst → PDF"}</div>
        </div>
        <div className="flex-1" />
        <button type="button" data-testid="report-ask-agent" className={btnGhost}
                onClick={() => window.dispatchEvent(new CustomEvent("cortex:open-harness", {
                  detail: {
                    harness: "orchestrator",
                    selectedAgent: { name: "reports", display_name: "Reports Assistant", icon: "📄" },
                    initialPrompt: `Work on the "${report.name}" report: `,
                  },
                }))}>
          <MessageSquare size={14} /> Ask the assistant
        </button>
        <button type="button" className={btnGhost} onClick={onRequestChange}>
          <Wrench size={14} /> Build a change
        </button>
        <button type="button" data-testid="report-render" className={btnPrimary}
                onClick={render} disabled={busy === "render"}>
          {busy === "render" ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} Render
        </button>
      </div>

      {diag && (
        <div className="rounded-md border border-red-500/40 bg-red-500/5 p-3">
          <div className="text-sm font-medium text-red-500 flex items-center gap-1.5">
            <AlertTriangle size={14} /> The template didn't compile
          </div>
          <pre className="mt-2 max-h-56 overflow-auto text-[11px] leading-snug font-mono text-page-text whitespace-pre-wrap">
            {diag}
          </pre>
        </div>
      )}

      {pdfUrl && (
        <div className="rounded-md border border-card-border bg-card-bg p-3 flex items-center gap-3">
          <Download size={14} className="text-nav-active" />
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
             className="text-sm text-nav-active" data-testid="report-pdf-link">Open the rendered PDF</a>
        </div>
      )}

      {/* ── TABS: the document first; plumbing on its own pages. Version history
             is NOT here — like apps/agents/models it lives in the row ⋮ menu. */}
      <div className="flex items-center gap-1 border-b border-card-border" data-testid="report-tabs">
        {[["document", "Document"], ["fields", "Fields"], ["params", "Parameters"],
          ["query", "Query"], ["template", "Template"]].map(([id, label]) => (
          <button key={id} type="button" data-testid={`report-tab-${id}`}
                  onClick={() => setTab(id)}
                  className={"px-3 py-2 text-sm border-0 bg-transparent cursor-pointer -mb-px border-b-2 " +
                    (tab === id ? "border-nav-active text-page-text font-medium"
                                : "border-transparent text-page-muted hover:text-page-text")}>
            {label}
          </button>
        ))}
      </div>

      {tab === "document" && (
        <div className="flex flex-col gap-4 flex-1 min-h-0">
          {/* WHICH SOURCES the report reads — auto-bound from the query's tables. */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold uppercase tracking-wide text-page-muted">Data sources</span>
            {(report.sources || []).length === 0 ? (
              <span className="text-xs text-page-muted">none bound yet</span>
            ) : (
              <span className="flex flex-wrap gap-1.5" data-testid="report-sources">
                {(report.sources || []).map((src) => (
                  <span key={src.source_id}
                        className="inline-flex items-center gap-1 rounded-full border border-card-border bg-transparent text-xs text-page-text px-2 py-1">
                    <Box size={12} className="opacity-70" /> {src.source_name || `source #${src.source_id}`}
                  </span>
                ))}
              </span>
            )}
          </div>
          {/* THE DOCUMENT, rendered with placeholder data by default. */}
          {previewUrl ? (
            <object data={previewUrl} type="application/pdf" data-testid="report-preview"
                    className="w-full flex-1 min-h-[520px] rounded-md border border-card-border">
              <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-nav-active">
                Open the layout preview
              </a>
            </object>
          ) : previewErr ? (
            <div className="text-xs text-page-muted">Layout preview unavailable: {previewErr.slice(0, 200)}</div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-page-muted">
              <Loader2 size={14} className="animate-spin" /> Rendering the layout preview…
            </div>
          )}
          <div className="text-xs text-page-muted">
            This is the template over sample data. <b>Render</b> produces the real document using
            the parameters on their tab.
          </div>
        </div>
      )}

      {tab === "fields" && (
        <div className="grid gap-2">
          <div className="text-xs text-page-muted">
            The template consumes exactly these columns; any query is validated against them
            before a PDF is produced.
          </div>
          {fields.length === 0 ? (
            <div className="text-xs text-page-muted">No fields declared.</div>
          ) : (
            <div className="flex flex-wrap gap-1.5" data-testid="report-fields">
              {fields.map((f) => (
                <span key={f.name} title={f.description || ""}
                      className="inline-flex items-center gap-1 rounded-full border border-card-border bg-transparent text-xs px-2 py-1">
                  <code className="font-mono text-page-text">{f.name}</code>
                  <span className="text-page-muted">{f.type}{f.required ? "" : "?"}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "params" && (
        <div className="grid gap-2">
          <div className="text-xs text-page-muted">What changes per run — set values here, then Render.</div>
          {declaredParams.length === 0 ? (
            <div className="text-xs text-page-muted">This report takes no parameters.</div>
          ) : (
            <div className="grid gap-2 md:grid-cols-2" data-testid="report-params">
              {declaredParams.map((prm) => (
                <label key={prm.name} className="grid gap-1">
                  <span className="text-xs text-page-muted">
                    <code className="font-mono">{prm.name}</code> {prm.type}{prm.required ? " *" : ""}
                    {prm.description ? ` — ${prm.description}` : ""}
                  </span>
                  <input
                    data-testid={`report-param-${prm.name}`}
                    className={inputCls}
                    value={paramValues[prm.name] ?? ""}
                    onChange={(e) => setParamValues((v) => ({ ...v, [prm.name]: e.target.value }))}
                  />
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "query" && (
        <div className="grid gap-2 flex-1 min-h-0">
          <div className="text-xs text-page-muted">
            SELECT with :param placeholders, reused every run. Sources auto-bind from its tables.
          </div>
          <textarea
            data-testid="report-query"
            className={inputCls + " font-mono text-xs flex-1 min-h-[300px]"}
            value={query}
            disabled={!canWrite}
            onChange={(e) => { setQuery(e.target.value); setDirty(true); }}
          />
        </div>
      )}

      {tab === "template" && (
        <div className="grid gap-2 flex-1 min-h-0">
          <div className="text-xs text-page-muted">Typst; `table.header` repeats on every page.</div>
          <textarea
            data-testid="report-template"
            className={inputCls + " font-mono text-xs flex-1 min-h-[380px]"}
            value={template}
            disabled={!canWrite}
            onChange={(e) => { setTemplate(e.target.value); setDirty(true); }}
          />
        </div>
      )}

      {canWrite && (tab === "query" || tab === "template") && (
        <div className="flex items-center gap-2">
          <button type="button" data-testid="report-save" className={btnPrimary}
                  onClick={save} disabled={!dirty || busy === "save"}>
            {busy === "save" ? <Loader2 size={14} className="animate-spin" /> : null}
            Save
          </button>
          {dirty && <span className="text-xs text-amber-600">Unsaved changes</span>}
        </div>
      )}
    </div>
  );
}

function CreateReportDialog({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await createReport({
        name: name.trim().toLowerCase(),
        display_name: displayName.trim() || null,
        description: description.trim() || null,
      });
      onCreated(r?.report);
    } catch (e) { setErr(String(e?.message || e)); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="New report" onClose={onClose}>
      <div className="grid gap-3">
        <div className="grid gap-1.5">
          <label className="text-xs text-page-muted">Name (lowercase, no spaces)</label>
          <input autoFocus data-testid="report-new-name" className={inputCls} value={name}
                 placeholder="monthly_statement"
                 onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="grid gap-1.5">
          <label className="text-xs text-page-muted">Display name</label>
          <input data-testid="report-new-display" className={inputCls} value={displayName}
                 placeholder="Monthly Statement" onChange={(e) => setDisplayName(e.target.value)} />
        </div>
        <div className="grid gap-1.5">
          <label className="text-xs text-page-muted">Description</label>
          <input className={inputCls} value={description}
                 onChange={(e) => setDescription(e.target.value)} />
        </div>
        {err && <div className="text-sm text-red-500">{err}</div>}
        <div className="flex items-center justify-end gap-2 pt-1">
          <button type="button" className={btnGhost} onClick={onClose}>Cancel</button>
          <button type="button" data-testid="report-new-create" className={btnPrimary}
                  onClick={submit} disabled={busy || !name.trim()}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create
          </button>
        </div>
      </div>
    </Modal>
  );
}
